import React, { useState, useRef, useEffect, useCallback } from "react";
import { useUsers, MockUser } from "../hooks/useExpiryItems";
import { DOCUMENT_CATEGORIES, OwnerChipInput } from "./ItemModal";
import {
  UserIcon,
  GridIcon,
  DownloadIcon,
  XCircleIcon,
  RefreshIcon,
} from "./Icons";

// ─── Types ────────────────────────────────────────────────────────────────────
type ActiveAction =
  | "renew"
  | "editExpiry"
  | "owner"
  | "category"
  | "snooze"
  | null;

const SNOOZE_OPTIONS = [
  { label: "1 day", days: 1 },
  { label: "3 days", days: 3 },
  { label: "7 days", days: 7 },
  { label: "2 weeks", days: 14 },
  { label: "1 month", days: 30 },
];

export interface BulkActionBarProps {
  selectedCount: number;
  onClear: () => void;
  onRenew: (newDate: string) => void;
  onEditExpiry: (newDate: string) => void;
  onAssignOwner: (ownerEmails: string[]) => void;
  onChangeCategory: (category: string) => void;
  onSnooze: (days: number) => void;
  onArchive: () => void;
  onExport: () => void;
  visible?: boolean;
}

// ─── Tiny shared primitives ───────────────────────────────────────────────────

/** Pill chip toggle */
const Chip: React.FC<{
  label: string;
  active: boolean;
  onClick: () => void;
}> = ({ label, active, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors whitespace-nowrap ${
      active
        ? "bg-[#2563EB] text-white border-[#2563EB]"
        : "bg-white text-[#374151] border-[#E5E7EB] hover:border-[#93C5FD] hover:bg-[#EFF6FF] hover:text-[#2563EB]"
    }`}
  >
    {label}
  </button>
);

/** Primary apply button */
const ApplyBtn: React.FC<{
  label?: string;
  onClick: () => void;
  disabled?: boolean;
  danger?: boolean;
}> = ({ label = "Apply", onClick, disabled, danger }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className={`px-4 py-2 rounded-md text-sm font-semibold transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
      danger
        ? "bg-[#FEF2F2] text-[#DC2626] border border-[#FCA5A5] hover:bg-[#FEE2E2]"
        : "bg-[#2563EB] text-white hover:bg-[#1D4ED8]"
    }`}
  >
    {label}
  </button>
);

// ─── Popover container ────────────────────────────────────────────────────────
const Popover: React.FC<{
  title: string;
  subtitle?: string;
  onClose: () => void;
  children: React.ReactNode;
}> = ({ title, subtitle, onClose, children }) => (
  <div className="absolute bottom-full mb-3 left-0 right-0 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 z-50 pointer-events-none">
    <div className="pointer-events-auto bg-white border border-[#E5E7EB] rounded-xl shadow-[0_8px_30px_rgba(0,0,0,0.12)] overflow-hidden">
      {/* Popover header */}
      <div className="flex items-start justify-between px-5 py-3.5 border-b border-[#F3F4F6] bg-[#FAFAFA]">
        <div>
          <p className="text-sm font-semibold text-[#111827]">{title}</p>
          {subtitle && (
            <p className="text-xs text-[#6B7280] mt-0.5">{subtitle}</p>
          )}
        </div>
        <button
          type="button"
          onClick={onClose}
          className="p-1 rounded text-[#9CA3AF] hover:text-[#374151] hover:bg-[#E5E7EB] transition-colors flex-shrink-0 ml-4"
          aria-label="Close"
        >
          <XCircleIcon className="h-4 w-4" />
        </button>
      </div>
      {/* Popover body */}
      <div className="px-5 py-4">{children}</div>
    </div>
  </div>
);

// ─── Main component ───────────────────────────────────────────────────────────
const BulkActionBar: React.FC<BulkActionBarProps> = ({
  selectedCount,
  onClear,
  onRenew,
  onEditExpiry,
  onAssignOwner,
  onChangeCategory,
  onSnooze,
  onArchive,
  onExport,
  visible = true,
}) => {
  const { data: allUsers = [] } = useUsers();

  const [active, setActive] = useState<ActiveAction>(null);
  const barRef = useRef<HTMLDivElement>(null);

  // ── Renew state ───────────────────────────────────────────────────────────
  const [renewDate, setRenewDate] = useState("");
  // ── Edit expiry date state ────────────────────────────────────────────────
  const [editExpiryDate, setEditExpiryDate] = useState("");

  // ── Owner state ───────────────────────────────────────────────────────────
  const [pickedOwners, setPickedOwners] = useState<string[]>([]);

  // ── Category state ────────────────────────────────────────────────────────
  const [pickedCategory, setPickedCategory] = useState("");
  const [customCategory, setCustomCategory] = useState("");

  // ── Snooze state ──────────────────────────────────────────────────────────
  const [snoozeDays, setSnoozeDays] = useState<number | null>(null);

  // Close popover on outside click
  useEffect(() => {
    if (!active) return;
    const handler = (e: MouseEvent) => {
      if (barRef.current && !barRef.current.contains(e.target as Node)) {
        setActive(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [active]);

  const toggle = useCallback((action: ActiveAction) => {
    setActive((prev) => {
      if (prev === action) return null;
      // Reset state when switching
      setRenewDate("");
      setEditExpiryDate("");
      setPickedOwners([]);
      setPickedCategory("");
      setCustomCategory("");
      setSnoozeDays(null);
      return action;
    });
  }, []);

  const close = useCallback(() => setActive(null), []);


  // Don't render if not visible or nothing selected
  if (!visible || selectedCount === 0) return null;

  // ── Action button style ───────────────────────────────────────────────────
  const btnCls = (action: ActiveAction) =>
    `relative inline-flex items-center gap-2 px-3.5 py-2 rounded-lg text-sm font-medium border transition-all ${
      active === action
        ? "bg-[#EFF6FF] text-[#2563EB] border-[#BFDBFE] shadow-sm"
        : "bg-white text-[#374151] border-[#E5E7EB] hover:bg-[#F9FAFB] hover:border-[#D1D5DB]"
    }`;

  return (
    <div ref={barRef} className="fixed bottom-0 left-0 right-0 z-40">
      {/* ── Popovers (rendered above bar) ─────────────────────────────────── */}

      {/* RENEW */}
      {active === "renew" && (
        <Popover
          title="Renew selected documents"
          subtitle={`Set a new expiry date for ${selectedCount} selected document${selectedCount !== 1 ? "s" : ""}`}
          onClose={close}
        >
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-end">
            <div className="flex-1">
              <label className="flex items-center gap-1.5 text-xs font-medium text-[#374151] mb-1.5">
                <svg
                  className="h-3.5 w-3.5 text-[#6B7280]"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                  />
                </svg>
                New expiry date
              </label>
              <input
                type="date"
                value={renewDate}
                onChange={(e) => setRenewDate(e.target.value)}
                min={new Date().toISOString().split("T")[0]}
                autoFocus
                className="block w-full max-w-xs border border-[#E5E7EB] rounded-md py-2 px-3 text-sm text-[#111827] bg-white"
              />
              {renewDate && (
                <p className="mt-1.5 text-xs text-[#6B7280]">
                  All {selectedCount} document{selectedCount !== 1 ? "s" : ""}{" "}
                  will be renewed to{" "}
                  <span className="font-medium text-[#111827]">
                    {new Date(renewDate + "T00:00:00").toLocaleDateString(
                      "en-US",
                      {
                        month: "long",
                        day: "numeric",
                        year: "numeric",
                      }
                    )}
                  </span>
                  . This will also reset their reminder cycles.
                </p>
              )}
            </div>
            <ApplyBtn
              label="Renew All"
              disabled={!renewDate}
              onClick={() => {
                onRenew(renewDate);
                close();
              }}
            />
          </div>
        </Popover>
      )}

      {/* EDIT EXPIRY DATE */}
      {active === "editExpiry" && (
        <Popover
          title="Edit expiry date"
          subtitle={`Correct the expiry date for ${selectedCount} selected document${selectedCount !== 1 ? "s" : ""} — logged as an edit, not a renewal`}
          onClose={close}
        >
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-end">
            <div className="flex-1">
              <label className="flex items-center gap-1.5 text-xs font-medium text-[#374151] mb-1.5">
                <svg
                  className="h-3.5 w-3.5 text-[#6B7280]"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                  />
                </svg>
                New expiry date
              </label>
              <input
                type="date"
                value={editExpiryDate}
                onChange={(e) => setEditExpiryDate(e.target.value)}
                autoFocus
                className="block w-full max-w-xs border border-[#2563EB] rounded-md py-2 px-3 text-sm text-[#111827] bg-white focus:outline-none focus:ring-1 focus:ring-[#2563EB]"
              />
              {editExpiryDate && (
                <p className="mt-1.5 text-xs text-[#6B7280]">
                  Expiry date updated for {selectedCount} document
                  {selectedCount !== 1 ? "s" : ""} to{" "}
                  <span className="font-medium text-[#111827]">
                    {new Date(editExpiryDate + "T00:00:00").toLocaleDateString(
                      "en-US",
                      {
                        month: "long",
                        day: "numeric",
                        year: "numeric",
                      }
                    )}
                  </span>
                  . Reminder cycles are not reset.
                </p>
              )}
            </div>
            <ApplyBtn
              label="Update All"
              disabled={!editExpiryDate}
              onClick={() => {
                onEditExpiry(editExpiryDate);
                close();
              }}
            />
          </div>
        </Popover>
      )}

      {/* ASSIGN OWNER */}
      {active === "owner" && (
        <Popover
          title="Assign owner(s)"
          subtitle={`Replace owners for ${selectedCount} selected document${selectedCount !== 1 ? "s" : ""}`}
          onClose={close}
        >
          <div className="space-y-3">
            <OwnerChipInput
              value={pickedOwners}
              onChange={setPickedOwners}
              users={allUsers as MockUser[]}
              allowExternalEmails={true}
            />
            {/* Footer */}
            <div className="flex items-center gap-3 pt-1">
              <ApplyBtn
                label={
                  pickedOwners.length > 0
                    ? `Assign ${pickedOwners.length} owner${pickedOwners.length !== 1 ? "s" : ""}`
                    : "Assign"
                }
                disabled={pickedOwners.length === 0}
                onClick={() => {
                  onAssignOwner(pickedOwners);
                  close();
                }}
              />
              {pickedOwners.length > 0 && (
                <button
                  type="button"
                  onClick={() => setPickedOwners([])}
                  className="text-xs text-[#9CA3AF] hover:text-[#374151] transition-colors"
                >
                  Clear
                </button>
              )}
            </div>
          </div>
        </Popover>
      )}

      {/* CHANGE CATEGORY */}
      {active === "category" && (
        <Popover
          title="Change category"
          subtitle={`Update the category for ${selectedCount} selected document${selectedCount !== 1 ? "s" : ""}`}
          onClose={close}
        >
          <div className="space-y-3">
            <div className="flex flex-wrap gap-2">
              {DOCUMENT_CATEGORIES.map((cat) => (
                <Chip
                  key={cat}
                  label={cat}
                  active={pickedCategory === cat}
                  onClick={() => {
                    setPickedCategory((prev) => (prev === cat ? "" : cat));
                    setCustomCategory("");
                  }}
                />
              ))}
              <Chip
                label="Custom…"
                active={pickedCategory === "__custom__"}
                onClick={() => {
                  setPickedCategory((prev) =>
                    prev === "__custom__" ? "" : "__custom__"
                  );
                  if (pickedCategory !== "__custom__") setCustomCategory("");
                }}
              />
            </div>
            {pickedCategory === "__custom__" && (
              <input
                type="text"
                value={customCategory}
                onChange={(e) => setCustomCategory(e.target.value)}
                placeholder="Enter custom category…"
                className="w-full mt-1 px-3 py-2 text-sm border border-[#E5E7EB] rounded-md placeholder:text-[#9CA3AF] focus:outline-none focus:ring-2 focus:ring-[#7C3AED]/20 focus:border-[#7C3AED]"
                autoFocus
              />
            )}
            <div className="flex items-center gap-3 pt-1">
              <ApplyBtn
                label={
                  pickedCategory === "__custom__"
                    ? customCategory.trim()
                      ? `Set "${customCategory.trim()}"`
                      : "Apply"
                    : pickedCategory
                      ? `Set "${pickedCategory}"`
                      : "Apply"
                }
                disabled={
                  !pickedCategory ||
                  (pickedCategory === "__custom__" && !customCategory.trim())
                }
                onClick={() => {
                  const finalCategory =
                    pickedCategory === "__custom__"
                      ? customCategory.trim()
                      : pickedCategory;
                  if (finalCategory) {
                    onChangeCategory(finalCategory);
                    close();
                  }
                }}
              />
            </div>
          </div>
        </Popover>
      )}

      {/* SNOOZE */}
      {active === "snooze" && (
        <Popover
          title="Snooze reminders"
          subtitle={`Pause reminder notifications for ${selectedCount} document${selectedCount !== 1 ? "s" : ""}`}
          onClose={close}
        >
          <div className="space-y-3">
            <div className="flex flex-wrap gap-2">
              {SNOOZE_OPTIONS.map(({ label, days }) => (
                <Chip
                  key={days}
                  label={label}
                  active={snoozeDays === days}
                  onClick={() =>
                    setSnoozeDays((prev) => (prev === days ? null : days))
                  }
                />
              ))}
            </div>
            {snoozeDays && (
              <p className="text-xs text-[#6B7280]">
                Reminders will resume in{" "}
                <span className="font-medium text-[#111827]">
                  {snoozeDays} day{snoozeDays !== 1 ? "s" : ""}
                </span>
                .
              </p>
            )}
            <ApplyBtn
              label={
                snoozeDays
                  ? `Snooze for ${snoozeDays} day${snoozeDays !== 1 ? "s" : ""}`
                  : "Apply"
              }
              disabled={snoozeDays === null}
              onClick={() => {
                if (snoozeDays !== null) {
                  onSnooze(snoozeDays);
                  close();
                }
              }}
            />
          </div>
        </Popover>
      )}

      {/* ── Action bar ────────────────────────────────────────────────────── */}
      <div className="bg-white border-t border-[#E5E7EB] shadow-[0_-2px_12px_rgba(0,0,0,0.06)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-14 gap-1.5 flex-wrap">
            {/* Selection pill */}
            <div className="flex items-center gap-2 mr-1 flex-shrink-0">
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#2563EB] text-white text-xs font-semibold rounded-full shadow-sm">
                <span className="h-4 w-4 rounded-full bg-white/25 flex items-center justify-center text-[10px] leading-none font-bold">
                  {selectedCount}
                </span>
                {selectedCount === 1 ? "item" : "items"} selected
              </span>
            </div>

            <div className="w-px h-6 bg-[#E5E7EB] flex-shrink-0 mx-1" />

            {/* ── Editing actions ── */}
            <button
              type="button"
              onClick={() => toggle("renew")}
              className={btnCls("renew")}
            >
              <RefreshIcon className="h-4 w-4" />
              Renew
              {active === "renew" && (
                <span className="absolute -top-1 -right-1 h-2 w-2 rounded-full bg-[#2563EB]" />
              )}
            </button>

            <button
              type="button"
              onClick={() => toggle("editExpiry")}
              className={btnCls("editExpiry")}
            >
              <svg
                className="h-4 w-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                />
              </svg>
              Edit Expiry Date
              {active === "editExpiry" && (
                <span className="absolute -top-1 -right-1 h-2 w-2 rounded-full bg-[#2563EB]" />
              )}
            </button>

            <button
              type="button"
              onClick={() => toggle("owner")}
              className={btnCls("owner")}
            >
              <UserIcon className="h-4 w-4" />
              Assign Owner
              {active === "owner" && (
                <span className="absolute -top-1 -right-1 h-2 w-2 rounded-full bg-[#2563EB]" />
              )}
            </button>

            <button
              type="button"
              onClick={() => toggle("category")}
              className={btnCls("category")}
            >
              <GridIcon className="h-4 w-4" />
              Category
              {active === "category" && (
                <span className="absolute -top-1 -right-1 h-2 w-2 rounded-full bg-[#2563EB]" />
              )}
            </button>

            <button
              type="button"
              onClick={() => toggle("snooze")}
              className={btnCls("snooze")}
            >
              <svg
                className="h-4 w-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              Snooze
              {active === "snooze" && (
                <span className="absolute -top-1 -right-1 h-2 w-2 rounded-full bg-[#2563EB]" />
              )}
            </button>

            <div className="w-px h-6 bg-[#E5E7EB] flex-shrink-0 mx-1" />

            {/* ── Utility actions ── */}
            <button
              type="button"
              onClick={() => {
                close();
                onExport();
              }}
              className="inline-flex items-center gap-2 px-3.5 py-2 rounded-lg text-sm font-medium border border-[#E5E7EB] bg-white text-[#374151] hover:bg-[#F9FAFB] transition-colors"
            >
              <DownloadIcon className="h-4 w-4 text-[#6B7280]" />
              Export CSV
            </button>

            <button
              type="button"
              onClick={() => {
                close();
                onArchive();
              }}
              className="inline-flex items-center gap-2 px-3.5 py-2 rounded-lg text-sm font-medium border border-[#FCA5A5] bg-[#FEF2F2] text-[#DC2626] hover:bg-[#FEE2E2] transition-colors"
            >
              <svg
                className="h-4 w-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4"
                />
              </svg>
              Archive
            </button>

            {/* Spacer + Clear */}
            <div className="flex-1" />
            <button
              type="button"
              onClick={() => {
                close();
                onClear();
              }}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-[#6B7280] hover:text-[#374151] hover:bg-[#F3F4F6] transition-colors flex-shrink-0"
            >
              <XCircleIcon className="h-4 w-4" />
              Deselect all
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkActionBar;
